import React, { useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Unstable_Grid2 as Grid } from '@mui/material'

import BuyBox from '@src/components/ProductInfo/BuyBox'
import SignInBanner from '@src/components/SignInBanner'
import { PdpFieldsFragment } from '@src/queries/PDPQuery.generated'
import {
  useResponsive,
  useResponsiveGridSpacing,
} from '@src/utils/useResponsiveSizes'

import { useMaterialSelection } from './hooks/useMaterialSelection'
import { useProductPricing } from './hooks/useProductPricing'
import { useViewPreference } from './hooks/useViewPreference'
import ViewProductOptionsDesktopView from './components/ViewProductOptionsDesktopView'
import ViewProductOptionsMobileView from './components/ViewProductOptionsMobileView'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import DiscontinuedProductAlert from '@src/components/DiscontinuedProductAlert'
import ProductPaMessage, {
  ProductPaMessageDisplayType,
} from '@src/components/ProductPriceAvailability/ProductPaMessage'
import { EventActionComponentType } from '@src/utils/analytics/enums'

const useStyles = makeStyles()((theme) => ({
  viewOptionsContainer: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(0),
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  signInBanner: {
    width: '100%',
    marginBottom: theme.spacing(6),
    paddingTop: `${theme.spacing(0)} !important`,
    paddingBottom: `${theme.spacing(0)} !important`,
  },
  viewOptionsItem: {
    paddingTop: `${theme.spacing(0)} !important`,
    paddingBottom: `${theme.spacing(0)} !important`,
    [theme.breakpoints.up('lg')]: {
      maxWidth: '510px',
    },
  },
  viewOptionsItemBuyBox: {
    paddingTop: `${theme.spacing(0)} !important`,
    paddingBottom: `${theme.spacing(0)} !important`,
    [theme.breakpoints.up('lg')]: {
      maxWidth: '290px',
    },
  },
  discontinuedMessage: {
    paddingTop: `${theme.spacing(0)} !important`,
    display: 'flex',
    justifyContent: 'center',
  },
}))
interface ViewProductPricingProps {
  product: PdpFieldsFragment & {
    brandKey: string
  }
  isBuyBoxSheetOpen: boolean
  setIsBuyBoxSheetOpen: (open: boolean) => void
  mobileSelectedMaterial: ValidMaterialPricingDetailFragment | null
  setMobileSelectedMaterial: (
    material: ValidMaterialPricingDetailFragment | null
  ) => void
  handleCloseBuyBoxSheet: () => void
}

const ViewProductPricing: React.FC<ViewProductPricingProps> = ({
  product,
  isBuyBoxSheetOpen,
  setIsBuyBoxSheetOpen,
  mobileSelectedMaterial,
  setMobileSelectedMaterial,
  handleCloseBuyBoxSheet,
}) => {
  const { classes } = useStyles()
  const { isMobile } = useResponsive()
  const getSpacing = useResponsiveGridSpacing()
  const [showAllOptions, setShowAllOptions] = useState(false)

  const {
    materialPricing,
    pricingError,
    pricingLoading,
    countryCode,
    isMultiCountryUser,
    discontinuedPricingInfo,
  } = useProductPricing(product)

  const { view, handleSelectView } = useViewPreference(product)

  const { selectedMaterial, setSelectedMaterial, handleSelect } =
    useMaterialSelection(materialPricing, pricingError)

  const commonProps = {
    materialPricing,
    pricingError,
    pricingLoading,
    view,
    handleSelectView,
    showAllOptions,
    setShowAllOptions,
  }

  if (isMobile) {
    return (
      <ViewProductOptionsMobileView
        {...commonProps}
        isBuyBoxSheetOpen={isBuyBoxSheetOpen}
        setIsBuyBoxSheetOpen={setIsBuyBoxSheetOpen}
        product={product}
        countryCode={countryCode}
        isMultiCountryUser={isMultiCountryUser}
        mobileSelectedMaterial={mobileSelectedMaterial}
        setMobileSelectedMaterial={setMobileSelectedMaterial}
        handleCloseBuyBoxSheet={handleCloseBuyBoxSheet}
        discontinuedPricingInfo={discontinuedPricingInfo}
      />
    )
  }

  return (
    <Grid
      container
      className={classes.viewOptionsContainer}
      justifyContent="space-between"
      spacing={getSpacing({ md: 10, lg: 0 })}
    >
      <Grid className={classes.signInBanner}>
        <SignInBanner />
      </Grid>
      {discontinuedPricingInfo && (
        <Grid md={12} className={classes.discontinuedMessage}>
          <DiscontinuedProductAlert
            discontinuedPricingInfo={discontinuedPricingInfo}
          />
        </Grid>
      )}
      {!discontinuedPricingInfo && product?.paMessage && (
        <Grid md={12}>
          <ProductPaMessage
            brandKey={product.brandKey}
            productNumber={product.productNumber}
            paMessage={product.paMessage}
            displayType={ProductPaMessageDisplayType.SRP}
            gaPricingOverrides={{
              component: EventActionComponentType.List,
            }}
          />
        </Grid>
      )}
      {!discontinuedPricingInfo && !product?.paMessage && (
        <>
          <Grid md={8} className={classes.viewOptionsItem}>
            <ViewProductOptionsDesktopView
              {...commonProps}
              selectedMaterial={selectedMaterial}
              setSelectedMaterial={setSelectedMaterial}
              handleSelect={handleSelect}
            />
          </Grid>
          <Grid md={4} lg className={classes.viewOptionsItemBuyBox}>
            <BuyBox
              isMultiCountryUser={isMultiCountryUser}
              loading={pricingLoading}
              error={pricingError}
              material={selectedMaterial}
              product={product}
              countryCode={countryCode}
              showPriceDisclaimer
              isPricingModal
              focus="products"
            />
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default ViewProductPricing
